import React, { PureComponent } from 'react';
import { getCurrentMission, getCurrentSession } from '../dataModelHelpers';
import MapMakerMap from './map/MapMakerMap';
import MapsMissionView from './mission/MapsMissionView';
import { AutoGenHelper } from './utils';
import { alertSuccess, alertError, alertWarn } from '../alertDispatcher';
import logger from '../logger';
import { AutoGenHelperParams, OnLoading, SettingsAccessLevel } from '../types/types';
import { LocalAtRecord, Attachment, Sample } from '../db';
import SlideoutTextBox from './mission/sampling/OperatorNotes';
import { convertProjection4329, wait } from '../utils';
import EventBus from '../EventBus';
import { dispatchMissionUpdated, MISSION_EVENTS } from '../missionEvents';
import { genPullinGeoJSON, updateBoundaryProperties } from './schedule/helpers/geojson';
import { MapAcresSummary, summarizeMapAcres } from './schedule/helpers/mapAcres';
import { Feature, GeometryObject, LineString } from 'geojson';
import {
  FieldShape,
  fieldGeometries,
  fieldLabelPoint,
  fieldOrderFeatures,
  shapeFromParts,
  shiftOrderByJobId,
} from './map/helpers/fieldOrder';
import * as Sentry from '@sentry/react';
import MapsExitInterview from './mission/maps/MapsExitInterview';
import { dispatchCoresNeedUpdate, dispatchSamplesUpdated, MAP_EVENTS } from '../mapEvents';
import { UITab } from './helpers/task';
import { Coordinate } from 'ol/coordinate';
import { JobsFieldSet } from '@rogoag/airtable/crm';
import { CHANGE_EVENTS, ChangeReasonPopupResult, ShowChangeTypeSelectorPopup } from '../changeReasonHelpers';
import ChangeTypeReasonSelector from './mission/sampling/SkipSampleReasonSelector';
import { restartPeriodicDbSync } from '../db/datamanager';
import { TaskName } from '../db/TaskClass';
import { IMissionLoader } from '../services/mission-loading/MissionLoaderCommon';
import OriginalMissionLoader from '../services/mission-loading/original/OriginalMissionLoader';
import { HistoricalLayersServiceImpl } from '../services/historical-layers/HistoricalLayersService';
import { getLocalAtSearch } from '../db_ops/airtable_search_ops';

interface MapsTaskProps {
  tab: string;
  task: TaskName;
  onLoading: OnLoading;
  accessLevel: SettingsAccessLevel;
  missionActive: string;
  updateCurrentTab: (tab: UITab) => void;
}

interface MapsTaskState {
  autogenHelper: AutoGenHelperParams | undefined;
  loadingSchedule: boolean;
  scheduleItems: LocalAtRecord<JobsFieldSet>[];
  mapmakingRulesDialogOpen: boolean;
  mapmakingExitInterviewOpen: boolean;
  mapmakingRules: string;
  scheduleFeatures: Feature<GeometryObject>[];
  shiftMapAcres?: MapAcresSummary;
  reasonSelectorOptions?: ShowChangeTypeSelectorPopup;
}

export default class MapsTask extends PureComponent<MapsTaskProps, MapsTaskState> {
  REVIEW_VIEW_NAME = 'App View - Review Missions';
  lastMissionID: string;
  historicalLayersService: HistoricalLayersServiceImpl;

  constructor(props: MapsTaskProps) {
    super(props);

    this.state = {
      loadingSchedule: false,
      scheduleItems: [],
      autogenHelper: undefined,
      mapmakingRulesDialogOpen: true,
      mapmakingExitInterviewOpen: false,
      mapmakingRules: '',
      scheduleFeatures: [],
    };

    this.historicalLayersService = new HistoricalLayersServiceImpl();
  }

  async componentDidMount() {
    restartPeriodicDbSync(this.props.task);

    EventBus.on(MISSION_EVENTS.CREATED_OR_DELETED, this.setMapmakingNotes);
    EventBus.on(MISSION_EVENTS.CREATED_OR_DELETED, this.generateGeoJSON);
    EventBus.on(MISSION_EVENTS.CREATED_OR_DELETED, this.preloadHistoricalLayers);
    EventBus.on(CHANGE_EVENTS.SHOW_CHANGE_REASON_SELECTOR, this.showSkipReasonSelectorPopup);
    const mission = getCurrentMission();
    const job = mission?.getJob();

    if (job) {
      this.setState({ mapmakingRules: job.map_making_rules });
    }

    // Preload historical layers if a mission is already loaded
    if (mission) {
      await this.preloadHistoricalLayers();
    }

    await this.loadSchedule();
  }

  componentWillUnmount() {
    EventBus.remove(MISSION_EVENTS.CREATED_OR_DELETED, this.setMapmakingNotes);
    EventBus.remove(MISSION_EVENTS.CREATED_OR_DELETED, this.generateGeoJSON);
    EventBus.remove(MISSION_EVENTS.CREATED_OR_DELETED, this.preloadHistoricalLayers);
    EventBus.remove(CHANGE_EVENTS.SHOW_CHANGE_REASON_SELECTOR, this.showSkipReasonSelectorPopup);
  }

  setMapmakingNotes = async () => {
    const mission = getCurrentMission();
    const job = mission?.getJob();
    const mapmakingRules = job?.map_making_rules || '';
    this.setState({
      mapmakingRulesDialogOpen: true,
      mapmakingRules,
    });
  };

  preloadHistoricalLayers = async () => {
    const mission = getCurrentMission();

    if (!mission) {
      return;
    }

    await mission.loadHistoricalMapLayers(this.historicalLayersService);
  };

  showSkipReasonSelectorPopup = (changePopupOptions: ShowChangeTypeSelectorPopup) => {
    this.setState({ reasonSelectorOptions: changePopupOptions });
  };

  setAutogenHelper = (autogenHelper: AutoGenHelperParams | undefined) => {
    this.setState({ autogenHelper });
  };

  closeNotesDialog = () => {
    this.setState({ mapmakingRulesDialogOpen: false });
    //localStorage.setItem('notesClosed', "true");
  };

  toggleNotesDialog = () => {
    this.setState({ mapmakingRulesDialogOpen: !this.state.mapmakingRulesDialogOpen });
  };

  openExitInterview = () => {
    this.setState({ mapmakingExitInterviewOpen: true });
  };

  loadMissionMaps = async (idOrJsonPayload: string) => {
    const trimmedPayload = idOrJsonPayload.trim();
    let id = trimmedPayload;

    if (!trimmedPayload.startsWith('rec')) {
      let payload: { jobId: string };

      try {
        payload = JSON.parse(trimmedPayload);
      } catch (e) {
        alertError('Wrong JSON');

        return;
      }

      id = payload.jobId;
    }

    console.log(`loadMissionMaps ${id}`);
    Sentry.setTag('job_info', id);

    const session = getCurrentSession();

    if (!session) {
      return;
    }

    await this.props.onLoading(async () => {
      const missionLoader: IMissionLoader = new OriginalMissionLoader();
      logger.log(`MapsTask: using the original mission loader to load job ${id}`);

      const loadedMission = await session.loadMission(missionLoader, id, false, this.setAutogenHelper);

      if (loadedMission) {
        this.props.updateCurrentTab(UITab.MAP);

        EventBus.dispatch(MAP_EVENTS.ZOOM_TO_MISSION);
        dispatchMissionUpdated();
        // Loading a mission from scratch introduces an entirely new set
        // of samples/cores — full core rebuild is required.
        dispatchCoresNeedUpdate();
      }
    });
  };

  /**
   * Loads the jobs from the schedule to be displayed
   * @param {boolean} forceRefresh Force airtable to pull from airtable api, pulls from cache by default
   */
  loadSchedule = async (forceRefresh?: boolean) => {
    const logTimer = logger.start('LOAD_SCHEDULE', { forceRefresh });
    this.setState({ loadingSchedule: true });

    try {
      console.time('MapsTask loadSchedule');
      const jobsQuery = await getLocalAtSearch<JobsFieldSet>('Jobs', this.REVIEW_VIEW_NAME, '', false);
      console.timeLog('MapsTask loadSchedule', 'jobsQuery');

      const shouldRefreshJobs = forceRefresh || jobsQuery?.getRecords().length === 0;

      if (shouldRefreshJobs) {
        await jobsQuery?.refresh({ waitForAttachments: false });
        console.timeLog('MapsTask loadSchedule', 'jobsQuery.refresh');
      }

      const jobs = jobsQuery?.getRecords() || [];
      console.timeLog('MapsTask loadSchedule', 'jobsQuery.getRecords');
      console.log(`MapsTask jobs.length = ${jobs.length}`);

      const labsQuery = await getLocalAtSearch('Labs', 'App View - Labs', ''); // search for all of the labs
      console.timeLog('MapsTask loadSchedule', 'labsQuery');

      const shouldRefreshLabs = forceRefresh || labsQuery?.getRecords().length === 0;

      if (shouldRefreshLabs) {
        await labsQuery?.refresh({ waitForAttachments: false });
        console.timeLog('MapsTask loadSchedule', 'labsQuery.refresh');
      }

      console.log(`MapsTask jobs.length=${jobs.length}`);
      this.setState({ scheduleItems: jobs });

      await this.generateGeoJSON(forceRefresh);
      console.timeEnd('MapsTask loadSchedule');

      if (shouldRefreshJobs) {
        alertSuccess('Schedule successfully refreshed');
      }

      await logger.stop(logTimer, { success: true });
    } catch (err) {
      console.error('MapsTask - loadSchedule - error', err);
      alertError('Schedule (m) failed to load. Check internet.');
      await logger.stop(logTimer, { success: false });
    }

    this.setState({ loadingSchedule: false });
  };

  generateGeoJSON = async (forceRefresh = false) => {
    const items = this.state.scheduleItems;
    const features: Feature<GeometryObject>[] = [];
    const mission = getCurrentMission();
    let shiftMapAcres: MapAcresSummary | undefined;

    if (mission || this.lastMissionID) {
      let currentMissionId: string | undefined = undefined;

      if (mission) {
        currentMissionId = mission.job_id;
      } else {
        currentMissionId = this.lastMissionID;
      }

      const currentMission = items.find((item) => item.get('Rogo Job ID') === currentMissionId);

      if (currentMission) {
        this.lastMissionID = currentMission.get('Rogo Job ID') || '';
      }

      let addedJobs: LocalAtRecord<JobsFieldSet>[] = [];
      // Sampled jobs are finished work: not drawn, but they hold their place in
      // the shift so the numbers on the fields ahead never shift.
      const sampledShiftJobs: LocalAtRecord<JobsFieldSet>[] = [];
      // Outline of each job's field, for the field order line. Keyed by job so
      // it survives the sort by 'Order' that happens after this loop.
      const shapesByJobId = new Map<string, FieldShape>();
      // Where each job wears its number, and where its hops meet it.
      const labelPointByJobId = new Map<string, [number, number] | null>();

      for (const item of items) {
        if (mission && item.get('Rogo Job ID') === mission.job_id) {
          // A sampled job is finished work and takes no place in the order.
          if (!item.get('Sample Date')) {
            addedJobs.push(item);
            // This job's boundary is being edited right now, so its outline
            // comes from the live mission rather than from the attachment.
            shapesByJobId.set(
              item.get('Rogo Job ID') ?? '',
              shapeFromParts(
                mission
                  .getFieldZones()
                  .flatMap((zone) => zone.to_feature())
                  .map((zoneFeature) => zoneFeature.geometry as GeometryObject),
                convertProjection4329([mission.pullin_lon, mission.pullin_lat]),
              ),
            );
          }

          continue;
        } else if (item.table === 'Jobs' && item.get('Shift') && currentMission) {
          const done = Boolean(item.get('Sample Date'));
          const jobShifts = item.get('Shift');
          const shift = jobShifts?.length ? jobShifts[0] : undefined;
          const currentMissionShifts = currentMission.get('Shift');
          const currentMissionShift = currentMissionShifts?.length ? currentMissionShifts[0] : undefined;

          if (done && currentMissionShift && shift === currentMissionShift) {
            sampledShiftJobs.push(item);
          }

          if (!done && currentMissionShift && shift === currentMissionShift) {
            if (forceRefresh) {
              await item.refresh();
            }

            const pullinGeoJSON = genPullinGeoJSON(item);
            let boundaryGeometries: GeometryObject[] = [];

            let result: Attachment | undefined;

            try {
              result = (await item.get_attachments('Bnd GeoJSON'))[0];
            } catch (error) {
              console.error('MapsTask - generateGeoJSON - get_attachments error', error);
            }

            if (!result) {
              await item.refresh();
              result = (await item.get_attachments('Bnd GeoJSON'))[0];
            }

            if (result) {
              const jsonText = await result.text();

              try {
                const boundaryGeoJSON = JSON.parse(jsonText);
                // Single the field out before updateBoundaryProperties renames
                // every feature — including the slow and unsafe zones — 'field'.
                const fieldParts = fieldGeometries(boundaryGeoJSON.features);
                updateBoundaryProperties(item, boundaryGeoJSON);
                features.push(...boundaryGeoJSON.features);
                // Same geometry objects, reprojected in place by the call above.
                boundaryGeometries = fieldParts;
              } catch (error) {
                alertWarn(`Boundary parse failure for ${item.get('Mission Name')}`);
                console.error(
                  'MapsTask - generateGeoJSON - boundary parse error',
                  item.id,
                  jsonText.slice(0, 100),
                  error,
                );
              }
            } else {
              if (!pullinGeoJSON.properties) {
                pullinGeoJSON.properties = {};
              }

              pullinGeoJSON.properties['noBoundary'] = true;
            }

            features.push(pullinGeoJSON as any);
            addedJobs.push(item);
            shapesByJobId.set(
              item.get('Rogo Job ID') ?? '',
              shapeFromParts(boundaryGeometries, pullinGeoJSON.geometry.coordinates),
            );
            // The number goes on the part the layer labels — the first — and a
            // job with no boundary falls back to its pullin so hops still reach it.
            labelPointByJobId.set(
              item.get('Rogo Job ID') ?? '',
              (boundaryGeometries[0] ? fieldLabelPoint(boundaryGeometries[0]) : null) ??
                (pullinGeoJSON.geometry.coordinates as [number, number]),
            );

            await wait(100);
          }
        }
      }

      if (currentMission?.get('Shift')?.length) {
        shiftMapAcres = summarizeMapAcres([...addedJobs, ...sampledShiftJobs]);
      }

      if (addedJobs.length > 1) {
        const coordinates: Coordinate[] = [];

        addedJobs = addedJobs.sort((a, b) => {
          return a.get('Order')! - b.get('Order')!;
        });

        let loadedMissionIdx = -1;

        for (const job of addedJobs) {
          if (job.get('Lat') && job.get('Lon')) {
            coordinates.push(
              convertProjection4329([parseFloat(job.get('Lon')!.toString()), parseFloat(job.get('Lat')!.toString())]),
            );
          }

          if (mission && job.get('Rogo Job ID') === mission.job_id) {
            loadedMissionIdx = coordinates.length - 1;
          }
        }

        if (coordinates.length > 1) {
          const orderLine: Feature<LineString> = {
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates,
            },
            properties: {
              loadedMissionIdx,
              complete: addedJobs.filter((job) => job.fields['Int Mission']).length === addedJobs.length,
            },
          };

          features.push(orderLine);
        }

        // Numbering runs over the whole shift, sampled jobs included, so a field
        // keeps the number the ops manager gave it for the length of the shift.
        // Sampled jobs carry no shape, so the hops pass straight over them.
        const orderedFields = [...addedJobs, ...sampledShiftJobs]
          .sort((a, b) => a.get('Order')! - b.get('Order')!)
          .map((job) => ({
            jobId: job.get('Rogo Job ID') ?? '',
            shape: shapesByJobId.get(job.get('Rogo Job ID') ?? '') ?? { rings: [] },
            labelPoint: labelPointByJobId.get(job.get('Rogo Job ID') ?? '') ?? null,
            done: sampledShiftJobs.includes(job),
          }));

        // Each field carries its own number, so stamp it on the job's features.
        const orderByJobId = shiftOrderByJobId(orderedFields);

        for (const feature of features) {
          const jobId = feature.properties?.['Rogo Job ID'];
          const order = orderByJobId.get(jobId);

          if (order && feature.properties) {
            feature.properties['Shift Order'] = order;
            feature.properties['Order Point'] = labelPointByJobId.get(jobId);
          }
        }

        features.push(...fieldOrderFeatures(orderedFields, mission?.job_id));
      }
    }

    console.log(`features has ${features.length} items`);

    this.setState({ scheduleFeatures: features.slice(), shiftMapAcres });
  };

  render() {
    return (
      <React.Fragment>
        {this.props.tab === 'mission' && (
          <MapsMissionView
            missionActive={this.props.missionActive}
            onLoading={this.props.onLoading}
            loadMissionMaps={this.loadMissionMaps}
            loadSchedule={this.loadSchedule}
            loadingSchedule={this.state.loadingSchedule}
            scheduleItems={this.state.scheduleItems}
            accessLevel={this.props.accessLevel}
            mapmakingNotesVisible={this.state.mapmakingRulesDialogOpen}
            toggleMapmakingNotes={this.toggleNotesDialog}
            lastMissionId={this.lastMissionID}
            openExitInterview={this.openExitInterview}
          />
        )}
        {this.props.tab === 'map' && (
          <MapMakerMap
            missionActive={this.props.missionActive}
            accessLevel={this.props.accessLevel}
            scheduleFeatures={this.state.scheduleFeatures}
            shiftMapAcres={this.state.shiftMapAcres}
            loadMissionMaps={this.loadMissionMaps}
            onLoading={this.props.onLoading}
            openExitInterview={this.openExitInterview}
            historicalLayersService={this.historicalLayersService}
          />
        )}

        {this.state.autogenHelper && <AutoGenHelper autogenHelper={this.state.autogenHelper} />}
        <SlideoutTextBox
          dialogOpen={Boolean(this.props.missionActive) && this.state.mapmakingRulesDialogOpen}
          message={this.state.mapmakingRules}
          closeDialog={this.closeNotesDialog}
          title="Mapmaking Notes:"
          expandedWidth="400px"
          customClass="mapmaking-notes"
        />
        {/* TODO normally I'd just let this be present and hidden all the time
                but it has a sublte dependency on being mounted with a mission loaded... */}
        {Boolean(this.props.missionActive) && (
          <MapsExitInterview
            open={this.state.mapmakingExitInterviewOpen}
            closeForm={() => this.setState({ mapmakingExitInterviewOpen: false })}
          />
        )}

        {/* This is a duplicate of the sampling change box. This isn't something we expected to use in maps mode
        but we do need it to move samples to alternate locations and log the change at the same time */}
        {Boolean(this.state.reasonSelectorOptions) && (
          <ChangeTypeReasonSelector
            onClose={async (result, { enteredReason, selectedChangeType }) => {
              const resultPayload: ChangeReasonPopupResult = { result, selectedChangeType, reason: enteredReason };
              EventBus.dispatch(CHANGE_EVENTS.CHANGE_REASON_SELECTOR_CLOSED, JSON.stringify(resultPayload));
              this.setState({ reasonSelectorOptions: undefined });
            }}
            clearBarcode={async function (sampleInstanceId: number): Promise<void> {
              const sampleId = Sample.get(sampleInstanceId)?.sample_id;

              if (sampleId) {
                dispatchSamplesUpdated([sampleId]);
              }

              return;
            }}
            {...this.state.reasonSelectorOptions}
          />
        )}
      </React.Fragment>
    );
  }
}
